import { markerScale } from '../shared/marker-size.js';
import { drawMarkerIcon } from './marker-icons.js';
import { landmarkMarker } from './landmark-markers.js';
import { isMarkerIconName } from '../shared/marker-icon.js';

export interface MarkerScreenPoint {
  x: number;
  y: number;
}

export interface MarkerScreenBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function getMarkerScreenSize(renderedWidth: number, icon = 'map-pin') {
  const scale = markerScale(renderedWidth);
  if (landmarkMarker(icon)) return { width: 44 * scale, height: 44 * scale };
  if (icon !== 'map-pin') return { width: 22 * scale, height: 22 * scale };
  return { width: 22 * scale, height: 31 * scale };
}

export function getMarkerScreenBounds(anchor: MarkerScreenPoint, renderedWidth: number, icon = 'map-pin'): MarkerScreenBounds {
  const size = getMarkerScreenSize(renderedWidth, icon);
  if (icon !== 'map-pin') {
    return {
      minX: anchor.x - size.width / 2,
      minY: anchor.y - size.height / 2,
      maxX: anchor.x + size.width / 2,
      maxY: anchor.y + size.height / 2,
    };
  }
  return {
    minX: anchor.x - size.width / 2,
    minY: anchor.y - size.height,
    maxX: anchor.x + size.width / 2,
    maxY: anchor.y,
  };
}

/** Draw the classic filled pin, or the Lucide icon selected in the picker. */
export function drawMapMarker(
  ctx: CanvasRenderingContext2D,
  anchor: MarkerScreenPoint,
  color: string,
  renderedWidth: number,
  icon = 'map-pin',
) {
  const size = getMarkerScreenSize(renderedWidth, icon);
  if (icon === 'map-pin' || !isMarkerIconName(icon)) {
    drawClassicMapPin(ctx, anchor, size.width / 22, color);
    return;
  }
  drawMarkerIcon(ctx, icon, anchor.x - size.width / 2, anchor.y - size.height / 2, size.width, color);
}

function drawClassicMapPin(ctx: CanvasRenderingContext2D, anchor: MarkerScreenPoint, scale: number, color: string) {
  const top = anchor.y - 31 * scale;
  const centerY = top + 10.5 * scale;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(anchor.x, anchor.y);
  ctx.bezierCurveTo(
    anchor.x - 2.5 * scale, anchor.y - 5 * scale,
    anchor.x - 11 * scale, top + 17 * scale,
    anchor.x - 11 * scale, centerY,
  );
  ctx.arc(anchor.x, centerY, 11 * scale, Math.PI, 0);
  ctx.bezierCurveTo(
    anchor.x + 11 * scale, top + 17 * scale,
    anchor.x + 2.5 * scale, anchor.y - 5 * scale,
    anchor.x, anchor.y,
  );
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, 2 * scale);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(anchor.x, centerY, 3.4 * scale, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.restore();
}
